import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import dayjs from 'dayjs';
import appConfig from 'config/app.config';
import {sortArrayByProperty} from 'helpers/array-helper';
import {
	getUniqueFacilitators, 
	filterFacilitatorsGamesAndGroupsByDate,
	getGeneralStats,
	getSurveyResult,
	getQuestionAnswers
} from 'helpers/stats-helper';
import {getText} from 'helpers/text-helper';
import {localizationData} from 'data/localization-data';
import {branchesData} from 'data/branches-data';
import {themesData} from 'data/themes-data';
import DatePicker, { registerLocale } from 'react-datepicker';
import Button from 'components/ui/button/button';
import Result from 'components/ui/result/result';
import './stats.scss';


const Stats = ({languageId, deviceInfo, goToPage}) => {

	/* Loading state */
	const [isLoading, setIsLoading] = useState(true);

	/* Games, archived games, groups */
	const [games, setGames] = useState([]);
	const [archivedGames, setArchivedGames] = useState([]);
	const [groups, setGroups] = useState([]);

	/* Selected branch */
	const [branchId, setBranchId] = useState(branchesData[0].id);
	const [themeId, setThemeId] = useState(themesData[0].id);

	/* Start and end dates */
	const [startDate, setStartDate] = useState(dayjs(new Date()).format('YYYYMMDD'));
	const [endDate, setEndDate] = useState(dayjs(new Date()).format('YYYYMMDD'));

	/* Selected theme data */
	const selectedThemeData = themesData.find((theme) => {return theme.id === themeId;});
	const themeResultSteps = (selectedThemeData && selectedThemeData.resultSteps
		? selectedThemeData.resultSteps.filter((step) => {return step.type === 'result';})
		: []
	);
	const themeQuestionSteps = (selectedThemeData && selectedThemeData.gameSteps
		? selectedThemeData.gameSteps.filter((step) => {return step.type === 'questions';})
		: []
	);


	/* Localize dates */
	registerLocale('da', {
		localize: {
			month: (n) => {return localizationData.months[n];},
			day: (n) => {return localizationData.days[n];}
		}, 
		formatLong:{
			date: () => {return 'yyyy-mm-dd';}
		} 
	});

	/* Combine games and archived games */
	const allGames = games.concat(archivedGames);
	

	/* Combine groups and archived groups */
	const allGroups = JSON.parse(JSON.stringify(groups));
	archivedGames.forEach((g) => {
		if (g.players) {
			g.players.forEach((p) => {
				allGroups.push(p);
			});
		}
	});

	/* Unique facilitators */
	const facilitators = getUniqueFacilitators(allGames);

	
	/* Filter by branch and dates */ 
	const {filteredGames, filteredGroups, filteredFacilitators} = 
		filterFacilitatorsGamesAndGroupsByDate(branchId, startDate, endDate, allGames, facilitators, allGroups);

	/* Get general game stats */
	const {
		totalStarted, 
		totalCompleted, 
		totalCompletedPercent, 
		totalGroups, 
		completedGroups, 
		completedGroupsPercent, 
		totalSurveyAnswers
	} = getGeneralStats(filteredGames, filteredGroups);


	const selectBranch = (newBranchId) => {
		if (newBranchId !== branchId) {
			const newThemeId = themesData.filter((theme) => {return theme.branchId === newBranchId;})[0].id;
			setBranchId(newBranchId);
			setThemeId(newThemeId);
		}
	};

	/**
	 * Get active games and groups
	 * @returns {Promise}
	 */
	const getGamesAndGroups = () => {
		const db = firebase.firestore();
		return new Promise((resolve)=> {
			db.collection(appConfig.groupsDbName).get().then((groupDocs) => {
				let groups = [];
				groupDocs.forEach((doc) => {groups.push({...doc.data(), id: doc.id});});
				db.collection(appConfig.gamesDbName).get().then((docs) => {
					let games = [];
					docs.forEach((doc) => {
						const gameObj = {
							...doc.data(), 
							players: groups.filter((g) => {return g.gameId === doc.id;}),
							id: doc.id
						};
						games.push(gameObj);
					});
					setGames(games);
					setGroups(groups);
					resolve({status: 'success', games, groups});
				}, (error) => {
					console.error('could not get games: ', error);
					resolve({status: 'error', error: error});
				});
			}).catch((error) => {
				console.error('could not get groups: ', error);
				resolve({status: 'error', error: error});
			});
		});
	};

	/**
	 * Get archived games and groups
	 * @returns {Promise}
	 */
	const getArchivedGames = () => {
		const db = firebase.firestore();
		return new Promise((resolve)=> {
			db.collection(appConfig.statsDbName).get().then((docs) => {
				let archivedGames = [];
				docs.forEach((doc) => {archivedGames.push({...doc.data()});});
				setArchivedGames(archivedGames);
				resolve({status: 'success', archivedGames});
			}, (error) => {
				console.error('could not get archived games: ', error);
				resolve({status: 'error', error: error});
			});
		});
	};


	/**
	 * Component mounted / will unmount
	 */
	useEffect(() => {
		/* Component mounted */ 
		Promise.all([getGamesAndGroups(), getArchivedGames()]).then((responses) => {
			let firstDate = dayjs(new Date()).format('YYYYMMDD');

			if (responses[0].status === 'success' && responses[1].status === 'success') {
				const allGames = responses[0].games.concat(responses[1].archivedGames);
				if (allGames.length > 0) {
					firstDate = dayjs(sortArrayByProperty(allGames, 'created', 'ASC')[0].created).format('YYYYMMDD');
				}
			}
			setStartDate(firstDate);
			setIsLoading(false);
		});

		/* Component will ummount */
		// return () => {};
	}, []);



	if (isLoading) {
		return (
			<div className="Stats">
				{/* Back btn */}
				<div className="Stats-backBtn">
					<Button 
						classes={['orange', 'fullHeight']}
						text={getText('stats_backBtn', languageId)} 
						onClick={() => {goToPage('settings');}} 
					/>
				</div>

				{/* Title */}
				<div className="Stats-title">{getText('stats_stats', languageId)}</div>

				{/* Loading icon */}
				<div className="Stats-loading" />
			</div>
		);
	}
		

	return (
		<div className="Stats">
			{/* Back btn */}
			<div className="Stats-backBtn">
				<Button 
					classes={['orange', 'fullHeight']}
					text={getText('stats_backBtn', languageId)} 
					onClick={() => {goToPage('settings');}} 
				/>
			</div>

			{/* Title */}
			<div className="Stats-title">
				<span>{getText('stats_stats', languageId)}</span>
			</div>

			{/* Period */}
			<div className="Stats-periodTitle">
				<span>{getText('stats_period', languageId)}</span>
			</div>
			<div className="Stats-period">
				<DatePicker
					disabled={false}
					selected={startDate ? dayjs(startDate).toDate() : new Date()}
					onChange={(date) => {setStartDate(dayjs(date).format('YYYYMMDD'));}}
					locale={'da'}
					dateFormat="dd/MM-yyyy"
					placeholderText="dd/MM-yyyy"
				/>
				<div className="Stats-periodTo"><span>{getText('stats_to', languageId)}</span></div>
				<DatePicker
					disabled={false}
					selected={endDate ? dayjs(endDate).toDate() : new Date()}
					onChange={(date) => {setEndDate(dayjs(date).format('YYYYMMDD'));}}
					locale={'da'}
					dateFormat="dd/MM-yyyy"
					placeholderText="dd/MM-yyyy"
				/>
			</div>

			{/* Facilitator stats */}
			<div className="Stats-section">
				<div className="Stats-columns">
					<div className="Stats-column">
						<div className="Stats-sectionRow">
							<span>{getText('stats_facilitators', languageId)}:</span>
							<span>{filteredFacilitators.length}</span>
						</div>
					</div>
				</div>
			</div>

			{/* Select branch */}
			<div className="Stats-selectBranch">
				{branchesData.map((branch) => {
					return (
						<div 
							key={branch.id} 
							className={'Stats-branchOption' + (branch.id === branchId ? ' selected' : '')}
						>
							<Button 
								classes={['purple', 'fullHeight']}
								text={getText(branch.title, languageId)} 
								onClick={() => {selectBranch(branch.id);}} 
							/>
						</div>
					);
				})}
			</div>

			{/* General stats */}
			<div className="Stats-section">
				<div className="Stats-columns">
					<div className="Stats-column">
						<div className="Stats-sectionRow">
							<span>{getText('stats_startedGames', languageId)}:</span><span>{totalStarted}</span>
						</div>
						<div className="Stats-sectionRow">
							<span>{getText('stats_finishedGames', languageId)}:</span>
							<span>{totalCompleted} ({totalCompletedPercent}%)</span>
						</div>
						<div className="Stats-sectionRow">
							<span>{getText('stats_surveyAnswers', languageId)}:</span>
							<span>{totalSurveyAnswers}</span>
						</div>
					</div>
					<div className="Stats-column">
						<div className="Stats-sectionRow">
							<span>{getText('stats_startedGroups', languageId)}:</span><span>{totalGroups}</span>
						</div>
						<div className="Stats-sectionRow">
							<span>{getText('stats_finishedGroups', languageId)}:</span>
							<span>{completedGroups} ({completedGroupsPercent}%)</span>
						</div>
					</div>
				</div>
			</div>

			{/* Themes */}
			<div className="Stats-section">
				<div className="Stats-themes">
					<div className="Stats-subtitle">
						<span>{getText('stats_themes', languageId)}</span>
					</div>
					{themesData.map((theme, index) => {
						if (theme.branchId !== branchId) return null;
						const numberOfGames = filteredGames.filter((g) => {
							return g.themeId === theme.id;
						}).length;
						const percentGames = (totalStarted > 0
							? Math.round(((numberOfGames / totalStarted) * 100.))
							: 0
						);
						return (
							<div key={index} className="Stats-sectionRow">
								<span>{getText(theme.title, languageId)}: </span>
								<span>{numberOfGames} ({percentGames}%)</span>
							</div>
						);
					})}
				</div>				
			</div>

			{/* Select theme */}
			<div className="Stats-selectTheme">
				{themesData.map((theme) => {
					if (theme.branchId !== branchId) return null;
					return (
						<div 
							key={theme.id} 
							className={'Stats-themeOption' + (theme.id === themeId ? ' selected' : '')}
						>
							<Button 
								classes={['purple', 'fullHeight']}
								text={getText(theme.title, languageId)} 
								onClick={() => {setThemeId(theme.id);}} 
							/>
						</div>
					);
				})}
			</div>

			{/* Theme survey results */}
			<div className="Stats-surveyResultsTitle">
				<span>{getText('stats_surveys', languageId)}</span>
			</div>
			<div className="Stats-surveyResults">
				{themeResultSteps.map((resultStep) => {
					const resultData = getSurveyResult(themeId, resultStep.id, filteredGames);
					if (!resultData) return null;
					return (
						<div key={resultStep.id} className="Stats-surveyResult">
							<Result 
								languageId={languageId}
								type="stats"
								resultId={resultStep.id}
								deviceInfo={deviceInfo}
								resultData={{
									type: 'result', 
									text: resultData.text,
									result: resultData
								}}
							/>
						</div>
					);
				})}
			</div>

			{/* Theme question answers */}
			<div className="Stats-questionAnswersTitle">
				<span>{getText('stats_questions', languageId)}</span>
			</div>
			<div className="Stats-questionAnswers">
				{themeQuestionSteps.map((questionStep) => {
					const questionAnswers = getQuestionAnswers(questionStep, filteredGroups);
					if (!questionAnswers) return null;
					return (
						<div key={questionStep.id} className="Stats-questionAnswer">
							<Result 
								languageId={languageId}
								type="stats"
								resultId={questionStep.id}
								deviceInfo={deviceInfo}
								resultData={{
									type: 'result', 
									text: questionStep.text,
									result: questionAnswers
								}}
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
};

Stats.propTypes = {
	languageId: PropTypes.string.isRequired,
	deviceInfo: PropTypes.object.isRequired,
	goToPage: PropTypes.func.isRequired,
};

export default Stats;